import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import {
  X, Printer, Loader2, TrendingUp, Eye, MousePointerClick,
  Users as UsersIcon, Calendar, MapPin, AlertTriangle,
} from 'lucide-react';

interface PoVData {
  ok: boolean;
  campaign: {
    id: string;
    slug: string;
    brand_name: string;
    headline: string | null;
    body: string | null;
    cta_text: string | null;
    placement: string;
    image_url: string | null;
    logo_url: string | null;
    background_color: string | null;
    starts_at: string;
    ends_at: string;
    invoice_status: string | null;
    invoice_amount_xcd: number | null;
    is_house_ad: boolean;
    corridor_ids: string[] | null;
    audience: string | null;
    hour_start: number | null;
    hour_end: number | null;
  };
  stats: {
    active_days: number;
    impressions: number;
    clicks: number;
    dismissals: number;
    /** Riders who opened the deal code (once per rider per day). */
    deal_views?: number;
    offer_code?: string | null;
    unique_users: number;
    click_through_rate: number | null;
    cost_per_thousand_impressions: number | null;
  };
  daily: { day: string; impressions: number; clicks: number }[];
  top_corridors: { corridor_id: string; impressions: number }[];
  generated_at: string;
}

const PLACEMENT_LABELS: Record<string, string> = {
  passenger_home_header: 'Passenger Home Header',
  passenger_sheet_idle_carousel: 'Passenger Sheet: Where To',
  passenger_sheet_confirm_carousel: 'Passenger Sheet: Confirm',
  passenger_sheet_active_ride_carousel: 'Passenger Sheet: Active Ride',
  commute_morning_takeover: 'Morning Commute Banner',
  commute_evening_takeover: 'Evening Commute Banner',
  ride_complete_full: 'Ride-Complete Takeover',
  weather_alert_banner: 'Weather Alert Banner',
  map_pin_featured: 'Featured Map Pin',
  festival_event_takeover: 'Festival Event Takeover',
  driver_idle_strip: 'Driver Idle Strip',
  splash_takeover: 'Splash Takeover',
  house_inbound_lead: 'House Lead',
};

function fmtDate(iso: string) {
  if (!iso) return '–';
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function fmtDateShort(iso: string) {
  if (!iso) return '–';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

interface ProofOfValuePrintProps {
  campaignId: string;
  onClose: () => void;
}

export default function ProofOfValuePrint({
  campaignId,
  onClose,
}: ProofOfValuePrintProps) {
  const { seesMoney } = useAdminAuth();
  const [data, setData] = useState<PoVData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: rpcData, error: rpcErr } = await supabase.rpc(
          'admin_campaign_proof_of_value',
          { p_campaign_id: campaignId },
        );
        if (cancelled) return;
        if (rpcErr) throw rpcErr;
        const blob = rpcData as PoVData;
        if (!blob || blob.ok === false) {
          throw new Error((blob as any)?.error || 'Failed to load campaign');
        }
        setData(blob);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Failed to load PoV');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campaignId]);

  const handlePrint = () => {
    window.print();
  };

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto print:bg-white print:p-0 print:relative print:overflow-visible"
      onClick={onClose}
    >
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #pov-printable, #pov-printable * { visibility: visible; }
          #pov-printable {
            position: absolute;
            left: 0; top: 0; right: 0;
            margin: 0; padding: 24px;
            box-shadow: none !important;
            background: white !important;
            color: #111 !important;
          }
          #pov-printable .pov-no-print { display: none !important; }
          #pov-printable .pov-page-break { page-break-after: always; }
          @page { margin: 16mm; size: A4 portrait; }
        }
      `}</style>

      <div
        className="bg-white text-gray-900 rounded-xl shadow-2xl max-w-[880px] w-full mt-8 mb-12 print:shadow-none print:rounded-none print:max-w-none print:m-0"
        id="pov-printable"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top toolbar — hidden when printing */}
        <div className="pov-no-print sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between z-10 rounded-t-xl">
          <div className="text-sm text-gray-500">
            Proof of Value · ready to print
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={onClose}
              className="border-gray-300 text-gray-700"
            >
              <X className="w-3.5 h-3.5 mr-1.5" /> Close
            </Button>
            <Button
              size="sm"
              onClick={handlePrint}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
              disabled={!data || loading}
            >
              <Printer className="w-3.5 h-3.5 mr-1.5" /> Print / Save PDF
            </Button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="p-12 flex items-center justify-center text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading campaign data…
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="p-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-red-900">
                  Couldn't generate Proof of Value
                </p>
                <p className="text-xs text-red-700 mt-1">{error}</p>
                <p className="text-xs text-red-700 mt-2">
                  Make sure migration{' '}
                  <code className="font-mono">
                    20260620530000_admin_campaign_proof_of_value.sql
                  </code>{' '}
                  has been applied to your Supabase project.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* The actual printable */}
        {!loading && data && (
          <div className="p-8 print:p-0 space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between border-b-2 border-gray-900 pb-5">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 rounded-md bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-xs">H</span>
                  </div>
                  <span className="text-sm tracking-widest uppercase text-gray-500 font-semibold">
                    H-Plate · Proof of Value
                  </span>
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mt-2">
                  Campaign report: {data.campaign.brand_name}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {PLACEMENT_LABELS[data.campaign.placement] ||
                    data.campaign.placement}
                </p>
              </div>
              {data.campaign.logo_url ? (
                <img
                  src={data.campaign.logo_url}
                  alt={data.campaign.brand_name}
                  className="w-20 h-20 rounded-full object-cover border border-gray-200"
                />
              ) : (
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center border border-gray-200"
                  style={{
                    backgroundColor: data.campaign.background_color || '#0f3d2e',
                  }}
                >
                  <span className="text-2xl font-bold text-white">
                    {data.campaign.brand_name.charAt(0)}
                  </span>
                </div>
              )}
            </div>

            {/* Headline summary */}
            <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 border border-emerald-200 rounded-xl p-5">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">
                    Flight period
                  </p>
                  <p className="font-semibold text-gray-900 flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-emerald-600" />
                    {fmtDate(data.campaign.starts_at)}
                  </p>
                  <p className="text-xs text-gray-500 ml-5.5 mt-0.5">
                    through {fmtDate(data.campaign.ends_at)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">
                    Active days
                  </p>
                  <p className="text-2xl font-bold text-emerald-700">
                    {data.stats.active_days}
                  </p>
                </div>
              </div>
            </div>

            {/* Big stats grid */}
            <div className="grid grid-cols-3 gap-4">
              <StatBlock
                label="Total impressions"
                value={data.stats.impressions.toLocaleString()}
                icon={<Eye className="w-5 h-5 text-emerald-600" />}
              />
              <StatBlock
                label="Total clicks"
                value={data.stats.clicks.toLocaleString()}
                icon={<MousePointerClick className="w-5 h-5 text-amber-600" />}
              />
              <StatBlock
                label="Unique viewers"
                value={data.stats.unique_users.toLocaleString()}
                icon={<UsersIcon className="w-5 h-5 text-cyan-600" />}
              />
              <StatBlock
                label="Click-through rate"
                value={
                  data.stats.click_through_rate !== null
                    ? `${data.stats.click_through_rate.toFixed(2)}%`
                    : '–'
                }
                icon={<TrendingUp className="w-5 h-5 text-rose-600" />}
                emphasis
              />
              {seesMoney && (
                <StatBlock
                  label="Effective CPM"
                  value={
                    data.stats.cost_per_thousand_impressions != null
                      ? `$${data.stats.cost_per_thousand_impressions.toFixed(2)} XCD`
                      : '–'
                  }
                  icon={<TrendingUp className="w-5 h-5 text-purple-600" />}
                />
              )}
              {seesMoney && (
                <StatBlock
                  label="Investment"
                  value={
                    data.campaign.invoice_amount_xcd
                      ? `$${data.campaign.invoice_amount_xcd.toLocaleString()} XCD`
                      : '–'
                  }
                  icon={<Calendar className="w-5 h-5 text-gray-600" />}
                />
              )}
              {data.stats.offer_code ? (
                <StatBlock
                  label={`Deal views (code ${data.stats.offer_code})`}
                  value={(data.stats.deal_views ?? 0).toLocaleString()}
                  icon={<MousePointerClick className="w-5 h-5 text-emerald-600" />}
                />
              ) : null}
            </div>

            {/* Daily impressions chart (simple bar list) */}
            {data.daily.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
                  Daily impressions
                </h2>
                <DailyBarChart points={data.daily} />
              </div>
            )}

            {/* Top corridors */}
            {data.top_corridors.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" /> Top corridors by impressions
                </h2>
                <div className="space-y-2">
                  {data.top_corridors.map((c) => {
                    const max = data.top_corridors[0]?.impressions || 1;
                    const pct = (c.impressions / max) * 100;
                    return (
                      <div key={c.corridor_id} className="text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-gray-700">{c.corridor_id}</span>
                          <span className="text-gray-500 font-mono text-xs">
                            {c.impressions.toLocaleString()}
                          </span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Creative recap */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
                Creative shown
              </h2>
              <div className="flex gap-4">
                {data.campaign.image_url && (
                  <img
                    src={data.campaign.image_url}
                    alt=""
                    className="w-32 h-32 rounded-lg object-cover border border-gray-200 shrink-0"
                  />
                )}
                <div className="text-sm text-gray-700 space-y-1">
                  {data.campaign.headline && (
                    <p className="font-semibold">{data.campaign.headline}</p>
                  )}
                  {data.campaign.body && <p>{data.campaign.body}</p>}
                  {data.campaign.cta_text && (
                    <p className="text-xs text-emerald-700 font-medium">
                      CTA: {data.campaign.cta_text}
                    </p>
                  )}
                  {data.campaign.audience && (
                    <p className="text-xs text-gray-500 mt-2">
                      Targeted: {data.campaign.audience}
                      {data.campaign.hour_start !== null &&
                      data.campaign.hour_end !== null &&
                      (data.campaign.hour_start !== 0 ||
                        data.campaign.hour_end !== 24)
                        ? ` · ${data.campaign.hour_start}:00–${data.campaign.hour_end}:00`
                        : ''}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 pt-4 flex items-center justify-between text-xs text-gray-500">
              <div>
                <p className="font-semibold text-gray-700">H-Plate Transit OS</p>
                <p>Dominica · {data.campaign.slug}</p>
              </div>
              <div className="text-right">
                <p>Generated {new Date(data.generated_at).toLocaleString()}</p>
                <p className="text-[10px] text-gray-400">
                  Stats are derived from `ad_events` and reflect impressions
                  served to authenticated H-Plate users.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatBlock({
  label,
  value,
  icon,
  emphasis,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  emphasis?: boolean;
}) {
  return (
    <div
      className={`rounded-lg p-4 border ${
        emphasis
          ? 'bg-emerald-600 border-emerald-700 text-white'
          : 'bg-white border-gray-200'
      }`}
    >
      <div
        className={`flex items-center gap-1.5 text-xs uppercase tracking-wider mb-1.5 ${
          emphasis ? 'text-emerald-100' : 'text-gray-500'
        }`}
      >
        {icon} {label}
      </div>
      <div
        className={`text-2xl font-bold ${
          emphasis ? 'text-white' : 'text-gray-900'
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function DailyBarChart({
  points,
}: {
  points: { day: string; impressions: number; clicks: number }[];
}) {
  const max = Math.max(1, ...points.map((p) => p.impressions));
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      <div className="flex items-end gap-1 h-32">
        {points.map((p) => {
          const h = Math.max(2, (p.impressions / max) * 100);
          return (
            <div
              key={p.day}
              className="flex-1 flex flex-col items-center gap-1 min-w-0"
              title={`${fmtDateShort(p.day)}: ${p.impressions} impressions, ${p.clicks} clicks`}
            >
              <div
                className="w-full bg-gradient-to-t from-emerald-600 to-cyan-400 rounded-t-sm relative group"
                style={{ height: `${h}%` }}
              >
                {/* Tiny click marker on top */}
                {p.clicks > 0 && (
                  <div
                    className="absolute -top-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-amber-500 rounded-full"
                    title={`${p.clicks} clicks`}
                  />
                )}
              </div>
              <span className="text-[8px] text-gray-500 font-mono whitespace-nowrap">
                {fmtDateShort(p.day)}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-end gap-3 mt-2 text-[10px] text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-emerald-500 rounded-sm" /> Impressions
        </span>
        <span className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" /> Click days
        </span>
      </div>
    </div>
  );
}
